import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {boolean} */
  #isLoading = false;

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton, quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (this.#isLoading) {
      minusButton.disabled = true;
      plusButton.disabled = true;
      quantityInput.disabled = true;
      return;
    }

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    quantityInput.disabled = false;
  }

  /**
   * Shows or hides the quantity update loading state.
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    this.#isLoading = Boolean(isLoading);
    this.classList.toggle('quantity-selector--loading', this.#isLoading);
    this.setAttribute('aria-busy', this.#isLoading ? 'true' : 'false');

    const status = this.refs.loadingStatus;
    if (status instanceof HTMLElement) {
      status.hidden = !this.#isLoading;
    }

    this.updateButtonStates();
  }

  /**
   * Prevent quantity changes while a cart update is in flight.
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    if (this.#isLoading) return;
    super.updateQuantity(stepMultiplier);
  }

  /**
   * @param {Event} event
   */
  setQuantity(event) {
    if (this.#isLoading) return;
    super.setQuantity(event);
  }

  /**
   * Broadcast quantity change and show loading while the cart request runs.
   */
  onQuantityChange() {
    this.setLoading(true);
    super.onQuantityChange();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
